import React, { useState } from 'react'; 
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  BarChart3, 
  Brain, 
  Camera, 
  Target, 
  Zap, 
  ShieldCheck,
  TrendingUp,
  Activity,
  Layers,
  Heart
} from 'lucide-react';

// --- COMPONENT IMPORTS ---
import LegalModal from '../components/LegalModal';

// --- ASSET IMPORTS ---
import Logo from '../assets/logo.png'; 
import Dash1 from '../assets/dashboard-preview-1.png';
import Dash2 from '../assets/dashboard-preview-2.png';
import Dash3 from '../assets/dashboard-preview-3.png';

import Acc1 from '../assets/Account-Overview-2.png';
import Acc2 from '../assets/Account-Overview-1.png';
import Acc3 from '../assets/Account-Overview-3.png';
import Acc4 from '../assets/Account-Overview-4.png';

import Jour1 from '../assets/journal-preview-1.png';
import Jour2 from '../assets/journal-preview-2.png';
import Jour3 from '../assets/journal-preview-3.png';

const LandingPage = ({ onGetStarted, onSupportClick, onLearnClick }) => {
  // State to handle Legal Modal (Used for internal triggers only now)
  const [legal, setLegal] = useState({ isOpen: false, type: 'terms' });

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  return (
    <div style={styles.container}>
      <style>{customGlobalStyles}</style>

      {/* --- REFINED HEADER / NAVIGATION --- */}
      <nav style={styles.navbar}>
        <div style={styles.navInner}>
          <div style={styles.navBrand}>
            <img src={Logo} alt="Logo" style={styles.logoImg} />
            <div style={styles.navSeparator} />
            <div style={styles.navHeaderText}>
              <span style={styles.navMainHeader}>Trading Journal for Focused Traders</span>
              <span style={styles.navSubHeader}>One stop journal for all markets</span>
            </div>
          </div>
          <button onClick={onGetStarted} style={styles.navActionBtn}>Launch Journal</button>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section style={styles.heroSection}>
        <div style={styles.glow} />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={styles.heroContent}
        >
          <div style={styles.indianBadge}>
            <Heart size={14} style={{ fill: '#ff9933', color: '#ff9933' }} /> 
            <span>Built By Indian Trader, For Indian Traders</span>
          </div>
          
          <h1 style={styles.mainTitle}>
            Trade With Discipline, <br />
            <span style={styles.accentText}>Not Emotion.</span>
          </h1>
          
          <div style={styles.quoteRotator}>
            <p>Trade Less, Execute Better • Find Your Edge • No More Guessing</p>
          </div>

          <p style={styles.heroSubtitle}>
            The professional journaling platform designed to help Indian traders 
            conquer the markets with data-driven confidence.
          </p>

          <div style={styles.ctaWrapper}>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onGetStarted}
              style={styles.primaryBtn}
            >
              Start Journaling Now <ArrowRight size={20} />
            </motion.button>
          </div>
        </motion.div>

        <div style={styles.tripleStackWrapper}>
          <motion.div initial={{ opacity: 0, x: -60, rotateY: 15 }} whileInView={{ opacity: 1, x: 0, rotateY: 25 }} transition={{ duration: 1 }} style={styles.stackCard}>
            <img src={Dash1} style={styles.actualImage} alt="Stats" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }} style={{...styles.stackCard, zIndex: 10, transform: 'scale(1.15)', border: '1px solid #38bdf8'}}>
            <img src={Dash2} style={styles.actualImage} alt="Main Dashboard" />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 60, rotateY: -15 }} whileInView={{ opacity: 1, x: 0, rotateY: -25 }} transition={{ duration: 1, delay: 0.4 }} style={styles.stackCard}>
            <img src={Dash3} style={styles.actualImage} alt="Trades List" />
          </motion.div>
        </div>
      </section>

      {/* --- ACCOUNT MANAGEMENT SECTION --- */}
      <section style={styles.section}>
        <div style={styles.splitGrid}>
          <motion.div {...fadeInUp} style={styles.textSide}>
            <h2 style={styles.sectionTitle}>One Command Center. <br/>All Your Accounts.</h2>
            <p style={styles.bodyText}>
              Whether it's your personal brokers account or multiple Prop Firm challenges, 
              keep your equity curves separate and your focus unified.
            </p>
            <div style={styles.featureList}>
                <div style={styles.vItem}><Layers color="#38bdf8" size={18}/> Multi-Account Separation</div>
                <div style={styles.vItem}><Target color="#38bdf8" size={18}/> Real-time Balance Tracking</div>
            </div>
          </motion.div>

          <div style={styles.cascadingWrapper}>
            {[Acc1, Acc2, Acc3, Acc4].map((img, index) => (
              <motion.div key={index} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: index * 0.15 }}
                style={{ ...styles.miniStackCard, marginLeft: `${index * 35}px`, marginTop: index === 0 ? '0' : '-50px', zIndex: index }}>
                <img src={img} style={styles.actualImage} alt={`Account ${index + 1}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- JOURNAL FORM SECTION --- */}
      <section style={{...styles.section, background: 'rgba(56, 189, 248, 0.02)'}}>
        <div style={styles.splitGrid}>
          <div style={styles.cascadingWrapper}>
            {[Jour1, Jour2, Jour3].map((img, index) => (
              <motion.div key={index} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: index * 0.15 }}
                style={{ ...styles.miniStackCard, marginLeft: `${index * 35}px`, marginTop: index === 0 ? '0' : '-60px', zIndex: index }}>
                <img src={img} style={styles.actualImage} alt={`Journal ${index + 1}`} />
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} style={styles.textSide}>
            <h2 style={styles.sectionTitle}>Stop Guessing. <br/>Start Executing.</h2>
            <p style={styles.bodyText}>
              Trade less, but better. Document your mental state to find out 
              exactly when you are prone to FOMO or revenge trading.
            </p>
            <div style={styles.featureList}>
                <div style={styles.vItem}><Brain color="#38bdf8" size={18}/> Emotional Analysis</div>
                <div style={styles.vItem}><Camera color="#38bdf8" size={18}/> High-Res Screenshot Vault</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- FINAL CTA SECTION --- */}
      <section style={styles.footerCTA}>
        <motion.div {...fadeInUp}>
          <h2 style={{ fontSize: '36px', marginBottom: '10px' }}>Join the Indian Trading Community</h2>
          <p style={{ color: '#94a3b8', marginBottom: '30px' }}>Your edge is waiting in your data.</p>
          <button onClick={onGetStarted} style={styles.finalBtn}>
            Join The Family <ArrowRight size={20} />
          </button>
        </motion.div>

        {/* --- FOOTER LINKS FOR GOOGLE VERIFICATION --- */}
        <div style={styles.bottomLinks}>
          <button onClick={onSupportClick} style={styles.footerLink}>Contact Us</button>

          {/* LEARN SECTION ADDED HERE */}
          <button onClick={onLearnClick} style={{...styles.footerLink, color: '#38bdf8'}}>
            Learn <span style={styles.newBadge}>NEW</span>
          </button>
          
          {/* STANDARD HTML LINKS: Essential for Google's Automated Scanners */}
          <a href="/terms" style={{...styles.footerLink, textDecoration: 'none'}}>
            Terms of Service
          </a>
          <a href="/privacy" style={{...styles.footerLink, textDecoration: 'none'}}>
            Privacy Policy
          </a>
        </div>
        <p style={styles.copyrightText}>© {new Date().getFullYear()} Focused Trader Journal. Built for performance.</p>
      </section>

      <LegalModal 
        isOpen={legal.isOpen} 
        type={legal.type} 
        onClose={() => setLegal({ ...legal, isOpen: false })} 
      />
    </div>
  );
};

const customGlobalStyles = `
  body { background-color: #000; margin: 0; font-family: 'Inter', sans-serif; }
  * { box-sizing: border-box; }
`;

const styles = {
  container: { background: '#000', color: '#fff', minHeight: '100vh', overflowX: 'hidden' },
  navbar: { padding: '12px 40px', borderBottom: '1px solid #1e293b', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', position: 'sticky', top: 0, zIndex: 1000 },
  navInner: { maxWidth: '1250px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { display: 'flex', alignItems: 'center', gap: '18px' },
  logoImg: { height: '36px', width: 'auto' },
  navSeparator: { height: '24px', width: '1px', background: '#334155' },
  navHeaderText: { display: 'flex', flexDirection: 'column', gap: '2px' },
  navMainHeader: { fontSize: '15px', fontWeight: '700', color: '#f8fafc', letterSpacing: '-0.2px' },
  navSubHeader: { fontSize: '10px', color: '#38bdf8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.8px' },
  navActionBtn: { background: 'transparent', color: '#fff', border: '1px solid #334155', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  heroSection: { padding: '80px 20px 60px 20px', textAlign: 'center', position: 'relative' },
  glow: { position: 'absolute', top: '0', left: '50%', transform: 'translateX(-50%)', width: '60vw', height: '60vw', background: 'radial-gradient(circle, rgba(56, 189, 248, 0.06) 0%, transparent 70%)', filter: 'blur(80px)', zIndex: 0 },
  heroContent: { position: 'relative', zIndex: 1, marginBottom: '60px' },
  indianBadge: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid #1e293b', padding: '10px 20px', borderRadius: '100px', fontSize: '12px', fontWeight: '700', color: '#fff', marginBottom: '32px' },
  mainTitle: { fontSize: 'clamp(36px, 6vw, 76px)', fontWeight: '900', lineHeight: 1, marginBottom: '15px', letterSpacing: '-2.5px' },
  accentText: { color: '#38bdf8' },
  quoteRotator: { fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '2.5px', marginBottom: '30px' },
  heroSubtitle: { fontSize: 'clamp(16px, 2vw, 20px)', color: '#94a3b8', maxWidth: '650px', margin: '0 auto 40px auto', lineHeight: '1.6' },
  primaryBtn: { background: '#38bdf8', color: '#000', padding: '18px 36px', borderRadius: '12px', fontSize: '18px', fontWeight: '900', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', margin: '0 auto', boxShadow: '0 10px 30px rgba(56, 189, 248, 0.3)' },
  tripleStackWrapper: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '20px', perspective: '1200px', padding: '0 20px' },
  stackCard: { width: '380px', background: '#0d1117', borderRadius: '12px', border: '1px solid #1e293b', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' },
  section: { padding: '100px 20px', maxWidth: '1200px', margin: '0 auto' },
  splitGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '80px', alignItems: 'center' },
  textSide: { textAlign: 'left' },
  sectionTitle: { fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: '800', marginBottom: '24px', lineHeight: '1.2' },
  bodyText: { color: '#94a3b8', fontSize: '18px', lineHeight: '1.7', marginBottom: '32px' },
  featureList: { display: 'flex', flexDirection: 'column', gap: '14px' },
  vItem: { display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '700', color: '#f8fafc', fontSize: '16px' },
  cascadingWrapper: { position: 'relative', padding: '20px 0' },
  miniStackCard: { width: '85%', background: '#0d1117', borderRadius: '10px', border: '1px solid #1e293b', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.6)' },
  actualImage: { width: '100%', height: 'auto', display: 'block' },
  footerCTA: { padding: '100px 20px 60px', textAlign: 'center', borderTop: '1px solid #1e293b' },
  finalBtn: { background: '#38bdf8', color: '#000', padding: '16px 32px', borderRadius: '10px', fontWeight: '900', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px', border: 'none', marginBottom: '60px' },
  bottomLinks: { display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
  footerLink: { background: 'none', border: 'none', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'color 0.2s', padding: '5px' },
  newBadge: { fontSize: '9px', background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', padding: '2px 6px', borderRadius: '4px', border: '1px solid rgba(56, 189, 248, 0.2)', marginLeft: '4px' },
  copyrightText: { color: '#475569', fontSize: '12px', fontWeight: '500' }
};

export default LandingPage;